import React, { useCallback, useEffect, useState } from "react";
import { X, ShieldCheck, Copy, Check, ExternalLink, Loader2, TriangleAlert, Sparkles, Heart } from "lucide-react";
import { toast } from "sonner";
import { WalletConnector } from "@/components/WalletConnector";
import { DonationSuccessAnimation } from "@/components/DonationSuccessAnimation";
import { SupportWall, OptInForm } from "@/components/SupportWall";
import {
  DONATION_ADDRESS,
  BSC_CHAIN,
  bscScanTxUrl,
  shortAddress,
} from "@/lib/walletService";
import { DONATION_STATES, donateWithWallet } from "@/lib/donationService";

const PRESETS = ["0.01", "0.05", "0.10", "0.25", "0.50", "1.00"];

/**
 * SupportProjectModal — the whole donation flow.
 *
 * Steps
 *   1) Intro + amount picker
 *   2) Wallet picker (WalletConnector) OR QR fallback
 *   3) Transaction state (awaiting signature → pending → success/error)
 *
 * Anything the user does is voluntary. There is no unlock, no premium,
 * and closing the modal always leaves the app fully functional.
 */
export const SupportProjectModal = ({ open, onClose }) => {
  const [amount, setAmount] = useState("0.05");
  const [custom, setCustom] = useState("");
  const [wallet, setWallet] = useState(null);
  const [account, setAccount] = useState(null);
  const [tx, setTx] = useState({ state: DONATION_STATES.IDLE, data: {} });
  const [copied, setCopied] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [wallRefreshKey, setWallRefreshKey] = useState(0);

  const effectiveAmount = custom.trim() ? custom.trim() : amount;

  useEffect(() => {
    if (!open) {
      // Reset transient state when the modal is closed
      setTx({ state: DONATION_STATES.IDLE, data: {} });
      setWallet(null);
      setAccount(null);
    }
  }, [open]);

  const busy = [
    DONATION_STATES.CONNECTING,
    DONATION_STATES.SWITCHING_CHAIN,
    DONATION_STATES.AWAITING_SIGNATURE,
    DONATION_STATES.PENDING,
  ].includes(tx.state);

  const handlePick = useCallback(async (w) => {
    setWallet(w);
    setTx({ state: DONATION_STATES.CONNECTING, data: {} });
    const result = await donateWithWallet({
      wallet: w,
      amount: effectiveAmount,
      onState: (evt) => setTx(evt),
    });
    if (result?.receipt && result?.success) {
      setShowSuccess(true);
    }
    if (result?.account) setAccount(result.account);
  }, [effectiveAmount]);

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(DONATION_ADDRESS);
      setCopied(true);
      toast.success("Dirección copiada");
      setTimeout(() => setCopied(false), 1400);
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-[65] flex items-end sm:items-center justify-center p-0 sm:p-4"
        style={{
          background:
            "radial-gradient(500px 400px at 50% 40%, rgba(91,46,255,0.28), transparent 60%), rgba(0,0,0,0.65)",
          backdropFilter: "blur(10px)",
        }}
        onClick={() => !busy && onClose?.()}
        data-testid="support-modal"
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className="relative w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl overflow-hidden"
          style={{
            background: "linear-gradient(180deg, #14101F, #0B0B12)",
            border: "1px solid rgba(138,107,255,0.3)",
            boxShadow: "0 0 60px rgba(91,46,255,0.25)",
          }}
        >
          {/* Header */}
          <header className="px-5 pt-5 pb-4 flex items-start gap-3 border-b border-white/6">
            <div
              className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
              style={{ background: "radial-gradient(closest-side, rgba(214,0,47,0.4), rgba(91,46,255,0.35))", border: "1px solid rgba(214,0,47,0.4)" }}
            >
              <Heart size={20} className="text-white" fill="currentColor" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.35em] text-[#8A6BFF]">Voluntario · sin ventajas</div>
              <h2 className="text-2xl font-extrabold tracking-tight leading-tight">
                Apoya <span style={{
                  background: "linear-gradient(180deg, #ff8798, #D6002F)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}>Esquiada</span>
              </h2>
              <p className="mt-1 text-[11px] text-white/60 leading-snug">
                Un proyecto independiente hecho con pasión por la comunidad del esquí.
              </p>
            </div>
            <button
              data-testid="support-close"
              disabled={busy}
              onClick={onClose}
              className="w-8 h-8 rounded-full hover:bg-white/6 flex items-center justify-center text-white/70 disabled:opacity-30"
            >
              <X size={16} />
            </button>
          </header>

          <div className="px-5 py-4 max-h-[70vh] overflow-y-auto">
            {/* Reassurance strip */}
            <ul className="text-[11px] text-white/60 space-y-1 mb-4">
              <li>· Donar no desbloquea contenido ni ventajas.</li>
              <li>· Tu wallet firma cada transacción — nunca pedimos claves ni frases.</li>
              <li>· La app funciona igual sin donar.</li>
            </ul>

            {/* What it supports */}
            <div
              className="rounded-2xl p-3 mb-4"
              style={{ background: "rgba(11,11,18,0.55)", border: "1px solid rgba(255,255,255,0.06)" }}
            >
              <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1.5">Tu apoyo ayuda a mantener</div>
              <div className="grid grid-cols-2 gap-1 text-[11px] text-white/80">
                {[
                  "APIs meteorológicas",
                  "Servidores",
                  "Infraestructura",
                  "Nuevas estaciones",
                  "Nuevas animaciones de Creep",
                  "Nuevas funciones",
                ].map((it) => (
                  <div key={it} className="flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-[#8A6BFF]" /> {it}
                  </div>
                ))}
              </div>
            </div>

            {/* Amount picker */}
            <div className="mb-4">
              <div className="text-[10px] uppercase tracking-widest text-[#8A6BFF] mb-2">Cantidad</div>
              <div className="grid grid-cols-3 gap-2" data-testid="amount-presets">
                {PRESETS.map((p) => (
                  <button
                    key={p}
                    data-testid={`amount-${p}`}
                    onClick={() => { setAmount(p); setCustom(""); }}
                    className={`py-2.5 rounded-2xl text-sm font-semibold border transition-colors ${
                      !custom && amount === p
                        ? "bg-[#5B2EFF]/25 border-[#8A6BFF]/70 text-white"
                        : "border-white/10 text-white/75 hover:border-[#8A6BFF]/40 hover:bg-white/5"
                    }`}
                  >
                    ❤ {p}
                  </button>
                ))}
              </div>
              <label className="mt-2 block">
                <span className="text-[10px] uppercase tracking-widest text-white/45">Cantidad personalizada</span>
                <div className="mt-1 flex items-center rounded-2xl border border-white/10 focus-within:border-[#8A6BFF]/60 bg-white/[0.04] px-3">
                  <input
                    data-testid="amount-custom"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={custom}
                    onChange={(e) => setCustom(e.target.value.replace(",", "."))}
                    className="flex-1 bg-transparent py-2.5 text-sm focus:outline-none placeholder:text-white/30"
                  />
                  <span className="text-xs text-white/50">BNB</span>
                </div>
              </label>
            </div>

            {/* Destination address preview */}
            <div
              className="mb-4 rounded-2xl p-3"
              style={{ background: "rgba(91,46,255,0.10)", border: "1px solid rgba(138,107,255,0.35)" }}
              data-testid="destination-preview"
            >
              <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] uppercase tracking-widest text-[#8A6BFF]">Destino oficial</div>
                <span className="text-[10px] text-white/60">BNB Smart Chain · id {BSC_CHAIN.chainIdInt}</span>
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck size={14} className="text-[#8A6BFF]" />
                <div className="text-[11px] font-mono text-white/90 break-all">{DONATION_ADDRESS}</div>
                <button
                  data-testid="destination-copy"
                  onClick={copyAddress}
                  className="w-8 h-8 rounded-full hover:bg-white/5 text-white/70 flex items-center justify-center shrink-0"
                  aria-label="Copiar dirección"
                >
                  {copied ? <Check size={14} className="text-emerald-300" /> : <Copy size={14} />}
                </button>
              </div>
            </div>

            {/* Transaction status */}
            {tx.state === DONATION_STATES.IDLE ? (
              <>
                <WalletConnector
                  amount={effectiveAmount}
                  onPick={handlePick}
                  connectedAccount={account}
                />
                <div className="mt-5">
                  <SupportWall compact refreshKey={wallRefreshKey} />
                </div>
              </>
            ) : tx.state === DONATION_STATES.SUCCESS ? (
              <div className="space-y-3" data-testid="tx-success-block">
                <TxStatus
                  state={tx.state}
                  data={tx.data}
                  account={account}
                  amount={effectiveAmount}
                  wallet={wallet}
                  onRetry={() => setTx({ state: DONATION_STATES.IDLE, data: {} })}
                  onClose={onClose}
                />
                <OptInForm
                  txHash={tx.data?.hash}
                  onDone={() => setWallRefreshKey((k) => k + 1)}
                />
              </div>
            ) : (
              <TxStatus
                state={tx.state}
                data={tx.data}
                account={account}
                amount={effectiveAmount}
                wallet={wallet}
                onRetry={() => setTx({ state: DONATION_STATES.IDLE, data: {} })}
                onClose={onClose}
              />
            )}
          </div>

          <footer className="px-5 py-3 border-t border-white/6 flex items-center gap-2">
            <ShieldCheck size={12} className="text-[#8A6BFF]" />
            <span className="text-[10px] text-white/50 leading-tight">
              Nunca pedimos claves privadas ni frases de recuperación. Toda transacción se firma en tu wallet.
            </span>
          </footer>
        </div>
      </div>

      {showSuccess && (
        <DonationSuccessAnimation
          hash={tx.data?.hash}
          onClose={() => setShowSuccess(false)}
        />
      )}
    </>
  );
};

const TxStatus = ({ state, data, account, amount, wallet, onRetry, onClose }) => {
  if (state === DONATION_STATES.SUCCESS) {
    return (
      <div className="text-center py-4" data-testid="tx-success">
        <div className="inline-flex w-14 h-14 rounded-full items-center justify-center mb-3"
          style={{ background: "radial-gradient(closest-side, rgba(91,46,255,0.6), transparent)" }}>
          <Sparkles size={20} className="text-[#8A6BFF]" />
        </div>
        <div className="font-extrabold text-lg">Donación confirmada</div>
        <div className="text-[11px] text-white/60 mt-1">La animación de Creep se abrirá enseguida.</div>
      </div>
    );
  }

  if (state === DONATION_STATES.ERROR) {
    return (
      <div className="rounded-2xl p-4 text-center" data-testid="tx-error"
        style={{ background: "rgba(214,0,47,0.08)", border: "1px solid rgba(214,0,47,0.35)" }}
      >
        <TriangleAlert size={22} className="mx-auto mb-2 text-[#D6002F]" />
        <div className="font-semibold text-white">Ha ocurrido un error</div>
        <div className="text-[11px] text-white/70 mt-1 break-words">{data.error || "Error inesperado"}</div>
        <div className="mt-3 flex gap-2 justify-center">
          <button className="creep-btn-ghost text-xs !py-1.5 !px-3" onClick={onRetry} data-testid="tx-retry">Reintentar</button>
          <button className="creep-btn-ghost text-xs !py-1.5 !px-3" onClick={onClose} data-testid="tx-close">Cerrar</button>
        </div>
      </div>
    );
  }

  if (state === DONATION_STATES.CANCELLED) {
    return (
      <div className="rounded-2xl p-4 text-center" data-testid="tx-cancelled"
        style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.10)" }}
      >
        <div className="font-semibold text-white">Donación cancelada</div>
        <div className="text-[11px] text-white/70 mt-1">No se ha enviado nada.</div>
        <div className="mt-3">
          <button className="creep-btn-ghost text-xs !py-1.5 !px-3" onClick={onRetry} data-testid="tx-retry">Volver a intentar</button>
        </div>
      </div>
    );
  }

  // Progress states
  const label =
    state === DONATION_STATES.CONNECTING ? "Conectando con la wallet…" :
    state === DONATION_STATES.SWITCHING_CHAIN ? "Cambiando a BNB Smart Chain…" :
    state === DONATION_STATES.AWAITING_SIGNATURE ? "Firma la transacción en tu wallet…" :
    "Esperando confirmación en la red…";

  return (
    <div className="rounded-2xl p-4" data-testid={state === DONATION_STATES.PENDING ? "tx-pending-ish" : "tx-progress"}
      style={{ background: "rgba(91,46,255,0.10)", border: "1px solid rgba(138,107,255,0.35)" }}
    >
      <div className="flex items-center gap-3">
        <Loader2 size={18} className="text-[#8A6BFF] animate-spin" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white">{label}</div>
          <div className="text-[11px] text-white/60 mt-0.5">
            {wallet?.name || "Wallet"} · {amount} BNB → {shortAddress(DONATION_ADDRESS)}
          </div>
          {account && (
            <div className="text-[10px] font-mono text-white/50 mt-0.5">Desde {shortAddress(account)}</div>
          )}
        </div>
      </div>

      {state === DONATION_STATES.PENDING && data.hash && (
        <a
          data-testid="tx-hash-link"
          href={bscScanTxUrl(data.hash)}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 flex items-center gap-1 text-[11px] text-[#8A6BFF] hover:text-white"
        >
          Ver en BscScan
          <ExternalLink size={11} />
        </a>
      )}
    </div>
  );
};

export default SupportProjectModal;
